// Hoja "RESUMEN ANUAL" del Excel "Control_Diario_Produccion" a partir de los consolidados
// guardados. Cada consolidado trae su fila del mes (columnas B…S y Z…AH, calculadas en el
// backend); aquí se calculan las columnas que cruzan meses (T…X var % vs mes anterior,
// Y acumulado del año) y la fila TOTAL AÑO, con las mismas fórmulas del Excel.
// Celda vacía del Excel = null.

import { MESES } from './controlDiarioCalculations';

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const N = (v) => (isNum(v) ? v : 0);
const INPUT_COLS = ['C', 'D', 'E', 'F', 'G', 'H', 'I'];

// Fila de un mes sin consolidado: equivale a una hoja del mes vacía en el Excel
const mesVacio = () => ({
  B: 0, C: 0, D: 0, E: 0, F: 0, G: 0, H: 0, I: 0,
  J: null, K: null, L: null, M: null, N: null, O: null, P: null, Q: null, R: null, S: null,
  Z: null, AA: null, AB: null, AC: 0, AD: 0, AE: null, AF: null, AG: null, AH: null,
});
const auxVacio = () => ({ fConD: 0, factConF: 0, factConG: 0, diasConHoras: 0 });

const varPct = (actual, anterior) => (N(anterior) === 0 || N(actual) === 0 ? null : (actual - anterior) / anterior);

export function computeResumenAnual(consolidados = []) {
  const byMonth = new Map(consolidados.map((c) => [c.month, c]));

  const rows = MESES.map((nombre, i) => {
    const month = i + 1;
    const c = byMonth.get(month) || null;
    return {
      month,
      nombre,
      consolidado: c,
      ...(c ? { ...mesVacio(), ...c.resumen } : mesVacio()),
      aux: c ? { ...auxVacio(), ...c.aux } : auxVacio(),
    };
  });

  // Columnas que cruzan meses
  let acumD = 0;
  rows.forEach((r, i) => {
    const prev = rows[i - 1];
    if (!prev) {
      r.T = r.U = r.V = r.W = r.X = null; // ENERO: "—" en el Excel
    } else {
      r.T = varPct(r.D, prev.D);
      r.U = varPct(r.G, prev.G);
      r.V = varPct(r.H, prev.H);
      r.W = varPct(r.P, prev.P);
      r.X = varPct(r.S, prev.S);
    }
    acumD += N(r.D);
    r.Y = r.B === 0 ? null : acumD;
  });

  // Fila TOTAL AÑO
  const t = {};
  ['B', ...INPUT_COLS].forEach((c) => { t[c] = rows.reduce((s, r) => s + N(r[c]), 0); });
  const aux = rows.reduce((s, r) => ({
    fConD: s.fConD + N(r.aux.fConD),
    factConF: s.factConF + N(r.aux.factConF),
    factConG: s.factConG + N(r.aux.factConG),
    diasConHoras: s.diasConHoras + N(r.aux.diasConHoras),
  }), auxVacio());
  const sumProd = (col) => rows.reduce((s, r) => s + N(r[col]) * N(r.I), 0);
  const horasDonde = (col) => rows.reduce((s, r) => (N(r[col]) > 0 ? s + N(r.I) : s), 0);

  t.J = t.C === 0 ? 0 : t.D / t.C;
  t.K = t.B === 0 ? 0 : t.D / t.B;
  t.L = t.I === 0 ? 0 : t.I / aux.diasConHoras;
  t.M = aux.fConD === 0 ? 0 : (aux.factConF - aux.fConD) / aux.fConD;
  t.N = t.G === 0 ? null : (aux.factConG === 0 ? null : (t.G - aux.factConG) / aux.factConG);
  t.O = t.I === 0 ? 0 : sumProd('O') / t.I;
  t.P = t.I === 0 ? 0 : sumProd('P') / t.I;
  t.Q = t.I === 0 ? 0 : sumProd('Q') / t.I;
  t.R = horasDonde('G') === 0 ? null : sumProd('R') / horasDonde('G');
  t.S = horasDonde('H') === 0 ? null : sumProd('S') / horasDonde('H');
  t.T = t.U = t.V = t.W = t.X = null;
  t.Y = t.D;
  t.Z = rows.reduce((s, r) => s + N(r.Z), 0);
  t.AA = t.G === 0 ? null : rows.reduce((s, r) => s + N(r.AA), 0);
  t.AB = t.G === 0 ? null : aux.factConG / t.G - 1;
  ['AC', 'AD', 'AE', 'AF', 'AG', 'AH'].forEach((c) => { t[c] = rows.reduce((s, r) => s + N(r[c]), 0); });

  return { rows, totals: t };
}

// Columnas de la hoja RESUMEN ANUAL (fila 5 del Excel)
export const RESUMEN_COLUMNS = [
  { col: 'B', header: 'Días registrados', nf: 'int', group: 'datos', desc: 'Días del mes que tienen kg de ciclos registrados.' },
  { col: 'C', header: 'N° Ciclos', nf: 'int', group: 'datos', desc: 'Ciclos de lavado del mes (no incluye los abortados).' },
  { col: 'D', header: 'Kg Ciclos', nf: 'one', group: 'datos', desc: 'Kg lavados en los ciclos del mes.' },
  { col: 'E', header: 'N° Lotes', nf: 'int', group: 'datos', desc: 'Lotes de ropa sucia recibidos en el mes.' },
  { col: 'F', header: 'Kg Lotes', nf: 'one', group: 'datos', desc: 'Kg de ropa sucia recibida (pesaje de la recepción).' },
  { col: 'G', header: 'Kg Alistamiento', nf: 'one', group: 'datos', desc: 'Kg de ropa limpia alistada en el mes (alistamientos no eliminados).' },
  { col: 'H', header: 'Prendas Alistadas', nf: 'int', group: 'datos', desc: 'Prendas alistadas en el mes.' },
  { col: 'I', header: 'Horas Autorizadas', nf: 'one', group: 'datos', desc: 'Horas de personal autorizadas en el mes.' },
  { col: 'J', header: 'Kg / Ciclo', nf: 'one', group: 'calculos', desc: 'Carga promedio por ciclo: Kg ciclos ÷ N° ciclos.' },
  { col: 'K', header: 'Kg Ciclos prom. / día', nf: 'one', group: 'calculos', desc: 'Kg ciclos ÷ días registrados.' },
  { col: 'L', header: 'Horas prom. / día', nf: 'one', group: 'calculos', desc: 'Horas autorizadas ÷ días que tienen horas registradas.' },
  { col: 'M', header: 'Var % Fact. vs Lotes', nf: 'pct', group: 'calculos', desc: '(Kg facturables − Kg lotes) ÷ Kg lotes, solo con los días que tienen ciclos y lotes. Positivo = se factura más de lo que se recibió.' },
  { col: 'N', header: 'Var % Alist. vs Fact.', nf: 'pct', group: 'calculos', desc: '(Kg alistados − Kg facturables) ÷ Kg facturables, solo con los días que tienen alistamiento.' },
  { col: 'O', header: 'Ciclos / Hora', nf: 'two', group: 'productividad', desc: 'Ciclos por hora autorizada (días con horas registradas).' },
  { col: 'P', header: 'Kg Fact. / Hora', nf: 'one', group: 'productividad', desc: 'Kg facturables por hora autorizada (días con horas registradas).' },
  { col: 'Q', header: 'Kg Lotes / Hora', nf: 'one', group: 'productividad', desc: 'Kg de lotes recibidos por hora autorizada.' },
  { col: 'R', header: 'Kg Alist. / Hora', nf: 'one', group: 'productividad', desc: 'Kg alistados por hora autorizada.' },
  { col: 'S', header: 'Prendas / Hora', nf: 'one', group: 'productividad', desc: 'Prendas alistadas por hora autorizada.' },
  { col: 'T', header: 'Var % Kg Ciclos vs mes ant.', nf: 'pct', group: 'calculos', vsAnterior: true, desc: 'Variación de los Kg ciclos frente al mes anterior. Vacío si alguno de los dos meses está en 0.' },
  { col: 'U', header: 'Var % Kg Alist. vs mes ant.', nf: 'pct', group: 'calculos', vsAnterior: true, desc: 'Variación de los Kg alistados frente al mes anterior.' },
  { col: 'V', header: 'Var % Prendas vs mes ant.', nf: 'pct', group: 'calculos', vsAnterior: true, desc: 'Variación de las prendas alistadas frente al mes anterior.' },
  { col: 'W', header: 'Var % Kg Fact./h vs mes ant.', nf: 'pct', group: 'calculos', vsAnterior: true, desc: 'Variación de los Kg facturables por hora frente al mes anterior.' },
  { col: 'X', header: 'Var % Prendas/h vs mes ant.', nf: 'pct', group: 'calculos', vsAnterior: true, desc: 'Variación de las prendas por hora frente al mes anterior.' },
  { col: 'Y', header: 'Acum. año Kg Ciclos', nf: 'one', group: 'calculos', desc: 'Suma de los Kg ciclos desde enero hasta este mes.' },
  { col: 'Z', header: 'Kg facturables', nf: 'one', group: 'conciliacion', desc: 'Kg ciclos × factor de carga con que se generó el consolidado del mes.' },
  { col: 'AA', header: 'Dif. Fact. − Alistado', nf: 'diff', group: 'conciliacion', desc: 'Kg facturables (días con alistamiento) − Kg alistados. Negativo = se alistó más de lo que se factura.' },
  { col: 'AB', header: '% Fact. vs Alistado', nf: 'pct', group: 'conciliacion', desc: 'Kg facturables (días con alistamiento) ÷ Kg alistados − 1.' },
  { col: 'AC', header: 'Días BAJO ALISTADO', nf: 'int', group: 'conciliacion', desc: 'Días en que los kg facturables quedaron por debajo de los kg alistados (se entregó más de lo que se cobra).' },
  { col: 'AD', header: 'Días SOBRE TOLERANCIA', nf: 'int', group: 'conciliacion', desc: 'Días en que los kg facturables superaron los kg alistados más la tolerancia del cliente.' },
  { col: 'AE', header: 'Kg dejados de cobrar', nf: 'one', group: 'conciliacion', desc: 'Kg alistados que no se alcanzan a facturar: máx(0, Kg alistados − Kg facturables).' },
  { col: 'AF', header: 'Kg potencial hasta tope', nf: 'one', group: 'conciliacion', desc: 'Kg que aún se podrían facturar hasta el tope del cliente: máx(0, Kg alistados × (1 + tolerancia del cliente) − Kg facturables).' },
  { col: 'AG', header: '$ dejado de cobrar', nf: 'money', group: 'conciliacion', desc: 'Kg dejados de cobrar × valor del kilo con que se generó el consolidado del mes.' },
  { col: 'AH', header: '$ potencial hasta tope', nf: 'money', group: 'conciliacion', desc: 'Kg potencial hasta tope × valor del kilo con que se generó el consolidado del mes.' },
];
